import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import { Link } from "react-router-dom";

import imagineRomana from "assets/images/imagine_romania.png";
import siglaGuvernului from "assets/images/sigla-guvernului.png";

// ── Paletă ──────────────────────────────────────────────────────────────────
const BLUE_PRIMARY = "#1565C0";
const BLUE_DARK = "#0d47a1";
const ICON_BOX_SOFT_BG = "#E8F0FE"; // fundalul casetelor mici pentru iconițe
const ICON_BOX_SOFT_ICON = "#1565C0";
const CARD_BG = "#ffffff";
const CARD_BORDER = "#e7ecf3";
const TITLE_COLOR = "#0f2c4a";
const DESC_COLOR = "#5a6b85";
const ARROW_COLOR = "#1565C0";

// Header dark pentru cardul Sesizări
const DARK_HEADER_BG = "#0f2c4a";
const DARK_HEADER_DOT = "rgba(255,255,255,0.18)";
const ACCENT_YELLOW = "#FFC107";

// Buton (dark blue cu hover)
const BTN_DARK = "#0f2c4a";
const BTN_DARK_HOVER = "#1a3a5c";

// ── Stiluri reutilizabile ───────────────────────────────────────────────────
const baseCardSx = {
  bgcolor: CARD_BG,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: "14px",
  boxShadow: "0 6px 18px rgba(15,44,74,0.06)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.25s ease, box-shadow 0.25s ease",
};

const linkCardSx = {
  ...baseCardSx,
  position: "relative",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 12px 28px rgba(15,44,74,0.12)",
    "& .arrow-cta": {
      color: ARROW_COLOR,
      transform: "translateX(4px)",
    },
  },
  "&:focus-visible": {
    outline: `3px solid ${BLUE_PRIMARY}`,
    outlineOffset: 3,
  },
};

const iconSquareSx = {
  width: { xs: 46, md: 52 },
  height: { xs: 46, md: 52 },
  borderRadius: "10px",
  background: `linear-gradient(135deg, ${BLUE_PRIMARY}, ${BLUE_DARK})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 4px 10px rgba(21,101,192,0.25)",
};

const innerIconSquareSx = {
  width: 42,
  height: 42,
  borderRadius: "10px",
  bgcolor: ICON_BOX_SOFT_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const submitBtnSx = {
  bgcolor: `${BTN_DARK} !important`,
  color: "#fff !important",
  textTransform: "uppercase",
  fontWeight: 700,
  letterSpacing: "0.04em",
  py: 1.25,
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(15,44,74,0.18)",
  "&:hover": {
    bgcolor: `${BTN_DARK_HOVER} !important`,
    boxShadow: "0 6px 16px rgba(15,44,74,0.25)",
  },
  "&:focus, &:focus:not(:hover)": {
    bgcolor: `${BTN_DARK} !important`,
    boxShadow: "0 0 0 3px rgba(15,44,74,0.3) !important",
  },
};

// ── Subcomponente ───────────────────────────────────────────────────────────
function InfoCard({ to, icon, title, description, ariaLabel }) {
  return (
    <SoftBox component={Link} to={to} aria-label={ariaLabel} sx={linkCardSx}>
      {/* Accent vertical subtil pe marginea stângă a cardului */}
      <SoftBox
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: { xs: 8, md: 10 },
          backgroundImage: "linear-gradient(135deg, #1565C0, #0d47a1)",
        }}
      />

      <SoftBox
        sx={{
          p: { xs: 2.5, md: 3 },
          pl: { xs: 3, md: 3.5 },
          display: "flex",
          gap: { xs: 2, md: 2.5 },
          alignItems: "flex-start",
          flexGrow: 1,
        }}
      >
        <SoftBox sx={iconSquareSx}>
          <Icon
            sx={{
              color: "#fff !important",
              fontSize: { xs: "1.5rem !important", md: "1.7rem !important" },
            }}
          >
            {icon}
          </Icon>
        </SoftBox>

        <SoftBox sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <SoftTypography
            sx={{
              color: TITLE_COLOR,
              fontWeight: 700,
              fontSize: { xs: "1.05rem", md: "1.2rem" },
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {title}
          </SoftTypography>
          <SoftTypography
            sx={{
              color: DESC_COLOR,
              fontSize: { xs: "0.85rem", md: "0.9rem" },
              lineHeight: 1.55,
              flexGrow: 1,
            }}
          >
            {description}
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      {/* Săgeată jos-dreapta */}
      <SoftBox
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: { xs: 2.5, md: 3 },
          pb: { xs: 2, md: 2.25 },
        }}
      >
        <Icon
          className="arrow-cta"
          sx={{
            color: ARROW_COLOR,
            fontSize: "1.5rem !important",
            transition: "color 0.2s ease, transform 0.2s ease",
          }}
        >
          arrow_forward
        </Icon>
      </SoftBox>
    </SoftBox>
  );
}

function SesizariSubItem({ icon, title, description, buttonText, to }) {
  return (
    <SoftBox sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <SoftBox sx={innerIconSquareSx}>
          <Icon sx={{ color: `${ICON_BOX_SOFT_ICON} !important`, fontSize: "1.25rem !important" }}>
            {icon}
          </Icon>
        </SoftBox>
        <SoftTypography
          sx={{
            color: TITLE_COLOR,
            fontWeight: 700,
            fontSize: { xs: "0.98rem", md: "1.05rem" },
          }}
        >
          {title}
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        sx={{
          color: DESC_COLOR,
          fontSize: { xs: "0.83rem", md: "0.88rem" },
          lineHeight: 1.6,
          mb: 2,
          flexGrow: 1,
        }}
      >
        {description}
      </SoftTypography>

      <SoftButton component={Link} to={to} fullWidth sx={submitBtnSx}>
        <Icon sx={{ mr: 1, fontSize: "1rem !important" }}>arrow_forward</Icon>
        {buttonText}
      </SoftButton>
    </SoftBox>
  );
}

InfoCard.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

SesizariSubItem.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
};

const ACCENT_ORANGE = "#FFC107";

const introBodySx = {
  color: DESC_COLOR,
  lineHeight: 1.75,
  "& strong": { color: TITLE_COLOR, fontWeight: 700 },
};

function PlatformIntroSection() {
  return (
    <SoftBox mb={{ xs: 4, md: 6 }}>
      {/* ── Sigla Guvernului — secțiune centrată, sus (toate ecranele) ── */}
      <SoftBox sx={{ textAlign: "center", mb: { xs: 2, md: 0 } }}>
        <SoftBox
          component="img"
          src={siglaGuvernului}
          alt="Guvernul României"
          sx={{ width: { xs: 76, md: 92 }, height: { xs: 76, md: 92 }, display: "inline-block" }}
        />
      </SoftBox>

      {/* ── Mobile: text centrat (fără imagine) ── */}
      <SoftBox sx={{ display: { xs: "block", md: "none" }, textAlign: "center" }}>
        <SoftBox maxWidth="800px" mx="auto">
          <SoftTypography
            sx={{
              ...introBodySx,
              fontSize: "0.92rem",
            }}
          >
            Platforma Națională <strong>&quot;Stop Rasism&quot;</strong> reprezintă un sistem oficial integrat
            de informare și de raportare a incidentelor antisemite, rasiste și xenofobe către
            autoritățile publice ale statului român. Procesul este proiectat pentru a fi accesibil,
            transparent și eficient, asigurând direcționarea sesizărilor către autoritățile publice
            competente.
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      {/* ── Desktop: text stânga + imagine România dreapta ── */}
      <Grid
        container
        spacing={4}
        alignItems="center"
        sx={{ display: { xs: "none", md: "flex" } }}
      >
        <Grid item md={6}>
          <SoftBox sx={{ pr: { md: 2, lg: 4 } }}>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <SoftBox
                aria-hidden="true"
                sx={{
                  width: 36,
                  height: 3,
                  bgcolor: ACCENT_ORANGE,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <SoftTypography
                sx={{
                  color: ACCENT_ORANGE,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "2.5px",
                  textTransform: "uppercase",
                }}
              >
                Platforma Națională
              </SoftTypography>
            </SoftBox>

            <SoftTypography
              component="h2"
              sx={{
                color: TITLE_COLOR,
                fontWeight: 700,
                fontSize: { md: "2.4rem", lg: "2.75rem" },
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                mb: 1.5,
              }}
            >
              Stop Rasism
            </SoftTypography>

            <SoftBox
              aria-hidden="true"
              sx={{
                width: 52,
                height: 3,
                bgcolor: ACCENT_ORANGE,
                borderRadius: "2px",
                mb: 2.5,
              }}
            />

            <SoftTypography
              sx={{
                ...introBodySx,
                fontSize: { md: "0.95rem", lg: "1rem" },
                textAlign: "left",
              }}
            >
              Platforma Națională <strong>&quot;Stop Rasism&quot;</strong> reprezintă un sistem oficial integrat
              de informare și de raportare a incidentelor antisemite, rasiste și xenofobe către
              autoritățile publice ale statului român. Procesul este proiectat pentru a fi accesibil,
              transparent și eficient, asigurând direcționarea sesizărilor către autoritățile publice
              competente.
            </SoftTypography>
          </SoftBox>
        </Grid>

        <Grid item md={6}>
          <SoftBox
            sx={{
              display: "flex",
              justifyContent: { md: "center", lg: "flex-end" },
              alignItems: "center",
            }}
          >
            <SoftBox
              component="img"
              src={imagineRomana}
              alt="Harta României — platformă națională Stop Rasism"
              sx={{
                width: "100%",
                maxWidth: { md: 580 },
                height: "auto",
                display: "block",
              }}
            />
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

// ── Componenta principală ───────────────────────────────────────────────────
function ServicesSection() {
  return (
    <SoftBox>
      <PlatformIntroSection />

      {/* Cele 2 carduri orizontale: Bibliotecă & Drepturi + Sesizări */}
      <Grid container spacing={{ xs: 4, md: 6 }} justifyContent="center">
        <Grid item xs={12} md={6}>
          <InfoCard
            to="/ce-trebuie-sa-stii"
            ariaLabel="Bibliotecă de informații — accesează secțiunea Ce trebuie să știi"
            icon="menu_book"
            title="Bibliotecă de informații"
            description="Informații esențiale despre incidentele motivate de ură, cadrul legal aplicabil și ce trebuie să știi înainte de a depune o sesizare."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            to="/drepturile-victimelor"
            ariaLabel="Drepturile victimelor — accesează secțiunea dedicată"
            icon="gavel"
            title="Drepturile victimelor"
            description="Află care sunt drepturile de care beneficiezi în calitate de victimă a unui incident rasist, antisemit sau xenofob și cum le poți exercita."
          />
        </Grid>

        {/* Cardul mare „Sesizări" */}
        <Grid item xs={12}>
          <SoftBox sx={baseCardSx}>
            {/* Header navy cu accent galben și dot pattern */}
            <SoftBox
              sx={{
                bgcolor: DARK_HEADER_BG,
                px: { xs: 2.5, md: 3 },
                py: { xs: 1.75, md: 2 },
                display: "flex",
                alignItems: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Dot pattern decorativ pe dreapta */}
              <SoftBox
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: { xs: 120, md: 200 },
                  backgroundImage: `radial-gradient(${DARK_HEADER_DOT} 1.5px, transparent 1.5px)`,
                  backgroundSize: "12px 12px",
                  pointerEvents: "none",
                  opacity: 0.7,
                  maskImage: "linear-gradient(90deg, transparent 0%, #000 100%)",
                  WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 100%)",
                }}
              />
              {/* Icoană galbenă */}
              <SoftBox
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Icon sx={{ color: `${ACCENT_YELLOW} !important`, fontSize: "1.7rem !important" }}>
                  assignment
                </Icon>
              </SoftBox>
              <SoftTypography
                sx={{
                  color: "#ffffff !important",
                  fontWeight: 700,
                  fontSize: { xs: "1.15rem", md: "1.3rem" },
                  letterSpacing: "0.005em",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Sesizări
              </SoftTypography>
            </SoftBox>

            {/* Conținut: 2 sub-secțiuni */}
            <SoftBox sx={{ p: { xs: 2.5, md: 3 }, flexGrow: 1 }}>
              <Grid container spacing={{ xs: 3, md: 4 }}>
                <Grid item xs={12} md={6}>
                  <SesizariSubItem
                    icon="description"
                    title="Formular sesizări"
                    description="Raportează incidente antisemite, rasiste sau xenofobe prin completarea formularului standardizat. Toate datele sunt procesate confidențial conform legislației GDPR."
                    buttonText="Completează formular"
                    to="/formular-sesizare"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SesizariSubItem
                    icon="search"
                    title="Verificare status"
                    description="Verifică stadiul procesării sesizării tale folosind codul unic primit. Poți urmări răspunsurile oficiale ale autorităților competente."
                    buttonText="Verifică status"
                    to="/status-sesizare"
                  />
                </Grid>
              </Grid>
            </SoftBox>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

export default ServicesSection;
